import dayjs from 'dayjs';

import Icon from '@components/atoms/icon';
import type { IconType } from '@components/atoms/icon';

type ScoreboardProps = {
  gameweeks: number[];
  selectedGameweek: number;
  selectedGameweekDeadline?: string | null;
  onSelectGameweek: (gameweek: number) => void;
  isLocked?: boolean;
};

const DEADLINE_FORMAT = 'ddd D MMM HH:mm';

const sortAscending = (a: number, b: number) => a - b;

const formatDeadline = (
  deadlineAt: string | null | undefined
): string | null => {
  if (!deadlineAt) return null;
  const parsed = dayjs(deadlineAt);
  return parsed.isValid() ? parsed.format(DEADLINE_FORMAT) : null;
};

type ChevronButtonProps = {
  iconName: IconType;
  onClick: () => void;
  isEnabled: boolean;
  ariaLabel: string;
  dataCy: string;
};

const ChevronButton = ({
  iconName,
  onClick,
  isEnabled,
  ariaLabel,
  dataCy
}: ChevronButtonProps) => (
  <button
    type="button"
    className={`flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 transition-all duration-150 ease-out active:scale-95 md:h-10 md:w-10 ${
      isEnabled
        ? 'cursor-pointer text-neutral-800 hover:bg-neutral-200'
        : 'cursor-not-allowed text-neutral-300'
    }`}
    onClick={onClick}
    data-cy={dataCy}
    aria-label={ariaLabel}
    disabled={!isEnabled}
  >
    <Icon name={iconName} className="text-[20px] md:text-[22px]" />
  </button>
);

export const Scoreboard = ({
  gameweeks,
  selectedGameweek,
  selectedGameweekDeadline,
  onSelectGameweek,
  isLocked = false
}: ScoreboardProps) => {
  const sortedGameweeks = [...gameweeks].sort(sortAscending);
  const currentIndex = sortedGameweeks.indexOf(selectedGameweek);

  const canDecrease = !isLocked && currentIndex > 0;
  const canIncrease =
    !isLocked &&
    currentIndex !== -1 &&
    currentIndex < sortedGameweeks.length - 1;

  const decreaseGameweek = () => {
    if (!canDecrease) return;
    onSelectGameweek(sortedGameweeks[currentIndex - 1]);
  };

  const increaseGameweek = () => {
    if (!canIncrease) return;
    onSelectGameweek(sortedGameweeks[currentIndex + 1]);
  };

  const formattedDeadline = formatDeadline(selectedGameweekDeadline);

  return (
    <div
      className="grid grid-cols-[36px_1fr_36px] items-center gap-2 px-2.5 py-2.5 md:grid-cols-[40px_1fr_40px] md:gap-4 md:px-4 md:py-3.5"
      data-cy="pick-scoreboard"
    >
      <ChevronButton
        iconName="ChevronLeft"
        onClick={decreaseGameweek}
        isEnabled={canDecrease}
        ariaLabel="Previous gameweek"
        dataCy="prev-gw"
      />
      <div className="flex flex-col items-center justify-center gap-[1px] text-center">
        <h3 className="mb-0 font-sans text-[19px] font-semibold leading-[1.2] text-neutral-800 md:text-2xl">
          Gameweek {selectedGameweek}
        </h3>
        {formattedDeadline && (
          <p
            className="mb-0 font-open-sans text-xs text-neutral-600 md:text-[13px]"
            data-cy="pick-scoreboard-deadline"
          >
            Deadline: {formattedDeadline}
          </p>
        )}
      </div>
      <ChevronButton
        iconName="ChevronRight"
        onClick={increaseGameweek}
        isEnabled={canIncrease}
        ariaLabel="Next gameweek"
        dataCy="next-gw"
      />
    </div>
  );
};
